/**
 * FlahaSOIL v2 — official platform theme (Phase 8C-A, refined Phase 10G).
 *
 * Centralizes the FlahaSOIL brand palette so every surface — landing,
 * shell, cards, charts — pulls from the same tokens. The palette is
 * earth-based and scientific and follows a strict colour rhythm:
 *
 *   - Brown  → brand structure only (app bar, hero/auth surfaces, logo).
 *   - Olive  → primary action, active nav, agronomic positive status.
 *   - Cream  → workspace background; white/cream → resting card surface.
 *   - Sand   → borders, dividers, table headers.
 *   - Blue / teal → scientific data, integrations, references, validation.
 *   - Amber / red → warnings and errors only.
 *
 * Visual identity goals: Scientific, Earth-based, Professional, Grounded,
 * Precise — a premium agronomic platform, not a developer dashboard.
 */
import { createTheme } from "@mui/material/styles";

import {
	motionTokens,
	reducedMotionMediaQuery,
	type FlahaMotionTokens,
} from "./motionTokens";

/**
 * Raw brand tokens (Phase 10F premium palette). Exported so non-MUI
 * surfaces can reuse them. The named scales below are the canonical
 * source of truth; the semantic aliases that follow keep older call
 * sites compiling while pointing them at the refined values.
 */
export const flahaSoilColors = {
	// Brown — brand structure only.
	brown950: "#2B1A12",
	brown900: "#3A2418",
	brown800: "#4A2B18",
	brown700: "#6F4320",
	// Olive — primary action / active nav / positive agronomic status.
	olive800: "#3F5724",
	olive700: "#4F6B2B",
	olive600: "#587332",
	olive500: "#6F8B3D",
	// Cream — workspace + card surfaces.
	cream50: "#FFFDF8",
	cream100: "#F8F5EE",
	cream200: "#F1E8D6",
	// Sand — borders, dividers, table headers.
	sand300: "#D8C6A3",
	sand400: "#BFA982",
	// Text.
	textPrimary: "#241A14",
	textSecondary: "#66594E",
	textMuted: "#8A7A68",
	// Blue / teal — scientific accents.
	blue700: "#1F6F8B",
	blue100: "#E8F4F7",
	teal700: "#2F7F73",
	teal100: "#E6F2EF",
	// Semantic.
	success: "#587332",
	warning: "#D88400",
	danger: "#B42318",
	info: "#1F6F8B",

	// Phase 10G workspace accents. These additive tokens leave the existing
	// light report palette stable until the report-specific redesign phase.
	flahaGreen: "#77A957",
	flahaPaBlue: "#4A9CC5",
	mineralBronze: "#A97742",
	workspaceCharcoal: "#0C100E",
	workspaceSoil: "#17120F",
	workspaceCream: "#F6F0E3",
	workspaceCreamSecondary: "#D9CFBE",
	workspaceCreamMuted: "#ADA18F",

	// ── Backward-compatible aliases (Phase 8C-A names → refined values) ──
	deepSoilBrown: "#3A2418",
	clayEarth: "#6F4320",
	sandBeige: "#D8C6A3",
	organicGreen: "#587332",
	organicGreenLight: "#6F8B3D",
	organicGreenDark: "#3F5724",
	analyticalCream: "#F1E8D6",
	mineralWarning: "#D88400",
	criticalSalinity: "#B42318",
	neutralBackground: "#F8F5EE",
	divider: "#D8C6A3",
} as const;

/** Status tokens for interpretation chips and warnings. */
export const flahaSoilStatus = {
	good: flahaSoilColors.success,
	fair: flahaSoilColors.warning,
	poor: flahaSoilColors.danger,
} as const;

/**
 * Scientific-accent tokens (Phase 10F). Blue/teal signal scientific
 * data, integrations, and reference/validation surfaces — never used
 * for primary action or agronomic status.
 */
export const flahaScientificAccent = {
	dataStrong: flahaSoilColors.blue700,
	dataSoft: flahaSoilColors.blue100,
	referenceStrong: flahaSoilColors.teal700,
	referenceSoft: flahaSoilColors.teal100,
} as const;

export interface FlahaWorkspacePalette {
	background: string;
	backgroundRaised: string;
	surface: string;
	surfaceStrong: string;
	border: string;
	borderStrong: string;
	textPrimary: string;
	textSecondary: string;
	textMuted: string;
	green: string;
	blue: string;
	bronze: string;
}

export interface FlahaGlassPalette {
	subtle: string;
	standard: string;
	strong: string;
	border: string;
	borderStrong: string;
	shadow: string;
	shadowRaised: string;
}

declare module "@mui/material/styles" {
	interface Palette {
		soil: {
			brown: string;
			clay: string;
			sand: string;
			cream: string;
		};
		workspace: FlahaWorkspacePalette;
		glass: FlahaGlassPalette;
	}
	interface PaletteOptions {
		soil?: {
			brown: string;
			clay: string;
			sand: string;
			cream: string;
		};
		workspace?: FlahaWorkspacePalette;
		glass?: FlahaGlassPalette;
	}
	interface Theme {
		motion: FlahaMotionTokens;
	}
	interface ThemeOptions {
		motion?: FlahaMotionTokens;
	}
}

export const flahaSoilTheme = createTheme({
	motion: motionTokens,
	palette: {
		// App chrome is the dark soil-intelligence workspace. Print/PDF
		// report surfaces own their own light styling separately.
		mode: "dark",
		// Olive drives primary action / active nav.
		primary: {
			main: flahaSoilColors.flahaGreen,
			light: flahaSoilColors.olive500,
			dark: flahaSoilColors.olive800,
			contrastText: flahaSoilColors.workspaceCharcoal,
		},
		// Brown carries brand structure.
		secondary: {
			main: flahaSoilColors.mineralBronze,
			light: flahaSoilColors.brown700,
			dark: flahaSoilColors.brown950,
			contrastText: flahaSoilColors.workspaceCream,
		},
		// Blue = scientific data / integrations / references.
		info: {
			main: flahaSoilColors.flahaPaBlue,
			contrastText: flahaSoilColors.workspaceCharcoal,
		},
		warning: {
			main: flahaSoilColors.warning,
			contrastText: flahaSoilColors.workspaceCharcoal,
		},
		error: {
			main: flahaSoilColors.danger,
			contrastText: "#FFFFFF",
		},
		success: {
			main: flahaSoilColors.flahaGreen,
			contrastText: flahaSoilColors.workspaceCharcoal,
		},
		background: {
			default: flahaSoilColors.workspaceCharcoal,
			paper: "#171D19",
		},
		text: {
			primary: flahaSoilColors.workspaceCream,
			// Match workspace secondary for MUI default text.secondary usage
			secondary: "#E4DAC8",
			disabled: "#C4B8A4",
		},
		divider: "rgba(246, 240, 227, 0.18)",
		soil: {
			brown: flahaSoilColors.brown900,
			clay: flahaSoilColors.brown700,
			sand: flahaSoilColors.sand300,
			cream: flahaSoilColors.cream200,
		},
		workspace: {
			background: flahaSoilColors.workspaceCharcoal,
			backgroundRaised: "#121815",
			surface: "#171D19",
			surfaceStrong: "#202820",
			// Slightly stronger borders for glass edge definition (a11y)
			border: "rgba(246, 240, 227, 0.18)",
			borderStrong: "rgba(246, 240, 227, 0.3)",
			textPrimary: flahaSoilColors.workspaceCream,
			// Secondary text lifted for contrast on glass
			textSecondary: "#E4DAC8",
			textMuted: "#C4B8A4",
			green: flahaSoilColors.flahaGreen,
			blue: flahaSoilColors.flahaPaBlue,
			bronze: flahaSoilColors.mineralBronze,
		},
		glass: {
			// Higher opacity = less wash-out of body copy over ambient bg
			subtle: "rgba(22, 28, 24, 0.72)",
			standard: "rgba(22, 28, 24, 0.86)",
			strong: "rgba(26, 33, 28, 0.94)",
			border: "rgba(246, 240, 227, 0.18)",
			borderStrong: "rgba(246, 240, 227, 0.3)",
			shadow: "0 16px 44px rgba(0, 0, 0, 0.28)",
			shadowRaised: "0 24px 72px rgba(0, 0, 0, 0.42)",
		},
	},
	shape: {
		borderRadius: 10,
	},
	typography: {
		fontFamily:
			'"Inter", "Helvetica Neue", "Segoe UI", Roboto, Arial, sans-serif',
		h1: { fontWeight: 700, letterSpacing: "-0.02em" },
		h2: { fontWeight: 700, letterSpacing: "-0.02em" },
		h3: { fontWeight: 700, letterSpacing: "-0.01em" },
		h4: { fontWeight: 700, letterSpacing: "-0.01em" },
		h5: { fontWeight: 600 },
		h6: { fontWeight: 600 },
		button: { fontWeight: 600, textTransform: "none", letterSpacing: "0.01em" },
		subtitle1: { fontWeight: 600 },
		body2: { lineHeight: 1.6 },
		caption: {
			letterSpacing: "0.015em",
			fontVariantNumeric: "tabular-nums lining-nums",
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				":root": {
					colorScheme: "dark",
				},
				body: {
					backgroundColor: flahaSoilColors.workspaceCharcoal,
					color: flahaSoilColors.workspaceCream,
				},
				"*:focus-visible": {
					outline: `3px solid ${flahaSoilColors.flahaPaBlue}`,
					outlineOffset: 3,
					boxShadow: `0 0 0 5px rgba(12, 16, 14, 0.9)`,
					transition: motionTokens.transition.focus,
				},
				[reducedMotionMediaQuery]: {
					"*, *::before, *::after": {
						animationDuration: "0.01ms !important",
						animationIterationCount: "1 !important",
						scrollBehavior: "auto !important",
						transitionDuration: "0.01ms !important",
					},
				},
			},
		},
		MuiAppBar: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(15, 20, 17, 0.9)",
					color: flahaSoilColors.workspaceCream,
				},
			},
		},
		MuiDrawer: {
			styleOverrides: {
				paper: {
					backgroundColor: "rgba(22, 28, 24, 0.96)",
					color: flahaSoilColors.workspaceCream,
					borderRight: "1px solid rgba(246, 240, 227, 0.14)",
				},
			},
		},
		MuiCard: {
			defaultProps: { variant: "outlined" },
			styleOverrides: {
				root: {
					borderColor: "rgba(246, 240, 227, 0.14)",
					backgroundColor: "rgba(26, 33, 28, 0.92)",
					backgroundImage:
						"linear-gradient(145deg, rgba(255,255,255,0.04), rgba(255,255,255,0) 48%)",
					color: flahaSoilColors.workspaceCream,
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					backgroundImage: "none",
					backgroundColor: "rgba(26, 33, 28, 0.92)",
					color: flahaSoilColors.workspaceCream,
				},
			},
		},
		MuiButton: {
			defaultProps: { disableElevation: true },
			styleOverrides: {
				root: {
					borderRadius: 8,
					transition: motionTokens.transition.surface,
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: { fontWeight: 500 },
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(32, 40, 32, 0.95)",
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				head: {
					fontWeight: 600,
					color: flahaSoilColors.workspaceCream,
					borderBottom: "1px solid rgba(246, 240, 227, 0.18)",
				},
				root: {
					color: flahaSoilColors.workspaceCreamSecondary,
					borderBottom: "1px solid rgba(246, 240, 227, 0.1)",
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				variant: "outlined",
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					backgroundColor: "rgba(12, 16, 14, 0.35)",
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: "rgba(246, 240, 227, 0.18)",
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: "rgba(246, 240, 227, 0.32)",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: flahaSoilColors.flahaPaBlue,
					},
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					color: flahaSoilColors.workspaceCreamSecondary,
				},
			},
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					fontVariantNumeric: "tabular-nums lining-nums",
				},
			},
		},
	},
});
